import type { OrderItem, OrderItemCharge } from '../components/details/types'

export interface ChargeRow {
  label: string
  amount: number
}

interface ChargeCarrier {
  productCode: string
  orderLine: number
  charges: OrderItemCharge[] | null
}

/**
 * Charge lines to render under an item's own price — one per charge display
 * name, e.g. "Alterations" (RP-5940).
 *
 * Two things the raw payload does not give us:
 *
 * - **Sign.** On a return order the item amounts are negative but MAO still
 *   sends `charges[].subTotal` unsigned, so a £24 alteration on a £-169 refund
 *   line has to render as £-24 for Subtotal / Total to reconcile with the lines
 *   above them.
 * - **Label.** `chargeDisplayName` is the customer-facing name and is what the
 *   ticket asks for; the old aggregated "Customizations" row hid which charge
 *   was being refunded.
 *
 * Zero-amount charges are dropped rather than rendered as a £0.00 row.
 */
export function resolveChargeRows(
  item: Pick<OrderItem, 'charges' | 'subTotal'>,
  /** Shown when MAO sends a charge with no display name. Translated by the caller. */
  fallbackLabel = 'Customizations',
): ChargeRow[] {
  if (!item.charges?.length) {
    return []
  }

  const isRefundLine = item.subTotal < 0
  const totalsByLabel = new Map<string, number>()

  for (const charge of item.charges) {
    const label = charge.chargeDisplayName?.trim() || fallbackLabel
    const magnitude = Math.abs(charge.subTotal)
    totalsByLabel.set(label, (totalsByLabel.get(label) ?? 0) + magnitude)
  }

  return [...totalsByLabel.entries()]
    .map(([label, magnitude]) => ({
      label,
      amount: isRefundLine ? -magnitude : magnitude,
    }))
    .filter((row) => Math.abs(row.amount) > 1e-6)
}

/**
 * Copies charges from the originating order's items onto the return order's
 * items that have none of their own (RP-5940).
 *
 * The return order's totals include the alteration amount, but its line items
 * are not guaranteed to carry the `charges` array that produced it — it is
 * confirmed present on the purchase order only. Rather than derive the missing
 * amount from the totals, take it from the order the customer returned from,
 * which is already in the React Query cache.
 *
 * Matched on `productCode` + `orderLine` first, then on `productCode` alone,
 * because a return order renumbers its lines. Items that already carry charges
 * are left untouched, so this becomes a no-op the day the payload includes them.
 */
export function backfillItemCharges<T extends ChargeCarrier>(
  items: T[],
  sourceItems: readonly ChargeCarrier[] | undefined,
): T[] {
  if (!sourceItems?.length) {
    return items
  }

  const withCharges = sourceItems.filter((item) => item.charges?.length)

  if (withCharges.length === 0) {
    return items
  }

  return items.map((item) => {
    if (item.charges?.length) {
      return item
    }

    const source =
      withCharges.find(
        (candidate) =>
          candidate.productCode === item.productCode &&
          candidate.orderLine === item.orderLine,
      ) ??
      withCharges.find(
        (candidate) => candidate.productCode === item.productCode,
      )

    return source ? { ...item, charges: source.charges } : item
  })
}
